import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import "../assets/css/material-kit.css";

const MobileApps = () => {
  const { t } = useTranslation();
  const services = [
    {
      title: t('mobileApps.services.android.title'),
      description: t('mobileApps.services.android.description'),
      icon: "🤖",
    },    
    {
      title: t('mobileApps.services.ios.title'),
      description: t('mobileApps.services.ios.description'),
      icon: "🍏",
    },
    {
      title: t('mobileApps.services.crossPlatform.title'),
      description: t('mobileApps.services.crossPlatform.description'),
      icon: "📱",
    },
  ];

  const industries = [
    {
      name: t('mobileApps.industries.retail.name'),
      description: t('mobileApps.industries.retail.description'),
      icon: "🛒", // Just the shopping cart emoji for now
      gradient: "linear-gradient(135deg, #1e293b, #334155)",
      accentColor: "#3b82f6"
    },
    {
      name: t('mobileApps.industries.management.name'),
      description: t('mobileApps.industries.management.description'),
      icon: "👥",
      gradient: "linear-gradient(135deg, #0f172a, #1e293b)",
      accentColor: "#6366f1"
    },
    {
      name: t('mobileApps.industries.food.name'),
      description: t('mobileApps.industries.food.description'),
      icon: "🍎",
      gradient: "linear-gradient(135deg, #374151, #4b5563)",
      accentColor: "#10b981"
    },
    {
      name: t('mobileApps.industries.healthcare.name'),
      description: t('mobileApps.industries.healthcare.description'),
      icon: "⚕️",
      gradient: "linear-gradient(135deg, #1f2937, #374151)",
      accentColor: "#06b6d4"
    },
    {
      name: t('mobileApps.industries.fintech.name'),
      description: t('mobileApps.industries.fintech.description'),
      icon: "💼",
      gradient: "linear-gradient(135deg, #111827, #1f2937)",
      accentColor: "#8b5cf6"
    },
    {
      name: t('mobileApps.industries.education.name'),
      description: t('mobileApps.industries.education.description'),
      icon: "📚",
      gradient: "linear-gradient(135deg, #0c1120, #1e293b)",
      accentColor: "#f59e0b"
    }
  ];

  const technologies = [
    { name: "React Native", icon: "⚛️" },
    { name: "React.js", icon: "⚛️" },
    { name: "Next.js", icon: "🚀" },
    { name: "Tailwind CSS", icon: "🎨" },
    { name: "TypeScript", icon: "📘" },
    { name: "JavaScript", icon: "💛" },
    { name: "FastAPI", icon: "🐍" },
    { name: "Python", icon: "🐍" },
    { name: "LangChain", icon: "🔗" },
    { name: "Elasticsearch", icon: "🔍" },
    { name: "Qdrant", icon: "🗄️" },
    { name: "PostgreSQL", icon: "🐘" },
    { name: "AWS", icon: "☁️" },
    { name: "Docker", icon: "🐳" },
    { name: "Git", icon: "📝" },
    { name: "GitHub", icon: "🐙" },
    { name: "Ubuntu", icon: "🐧" },
    { name: "Linux", icon: "🐧" },
    { name: "Android", icon: "🤖" },
    { name: "iOS", icon: "🍎" },
  ];

  return (
    <div className="mobile-apps">
      {/* Hero Section */}
      <section className="py-7 bg-gradient-primary">
        <div className="container">
          <div className="row">
            <div className="col-lg-8 mx-auto text-center">
              <h1 className="text-white mb-4 display-4">
                {t('mobileApps.title')}
              </h1>
              <p className="text-white lead mb-0">
                {t('mobileApps.subtitle')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-7">
        <div className="container">
          <div className="row text-center">
            {services.map((service, index) => (
              <div key={index} className="col-md-4 mb-4">
                <div className="card h-100 shadow-sm hover-card">
                  <div className="card-body text-center p-4">
                    <div className="mb-3" style={{ fontSize: '3rem' }}>
                      {service.icon}
                    </div>
                    <h5 className="card-title font-weight-bold mb-3">
                      {service.title}
                    </h5>
                    <p className="card-text text-muted">
                      {service.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries Section */}
      <section className="py-7" style={{ backgroundColor: '#f8fafc' }}>
        <div className="container">
          <div className="row mb-5">
            <div className="col-12 text-center">
              <h2 className="mb-4 font-weight-bold text-dark">{t('mobileApps.industries.title')}</h2>
              <p className="lead mb-0" style={{ color: '#64748b' }}>
                {t('mobileApps.industries.subtitle')}
              </p>
            </div>
          </div>
          <div className="row">
            {industries.map((industry, index) => (
              <div key={index} className="col-lg-4 col-md-6 mb-4">
                <div className="card h-100 border-0 hover-card position-relative overflow-hidden" 
                     style={{ 
                       backgroundColor: 'white',
                       boxShadow: '0 4px 20px rgba(15, 23, 42, 0.08)',
                       border: '1px solid #e2e8f0'
                     }}>
                  <div className="card-body text-center p-5 position-relative">
                    <div className="mb-4">
                      <div 
                        className="d-inline-flex align-items-center justify-content-center rounded-circle"
                        style={{ 
                          width: '80px', 
                          height: '80px', 
                          background: industry.gradient,
                          boxShadow: '0 8px 30px rgba(15, 23, 42, 0.2)',
                          border: `3px solid ${industry.accentColor}20`
                        }}
                      >
                        <div style={{ fontSize: '2.2rem' }}>
                          {industry.icon}
                        </div>
                      </div>
                      <div 
                        className="mt-3 mx-auto"
                        style={{
                          width: '40px',
                          height: '3px',
                          background: industry.accentColor,
                          borderRadius: '2px'
                        }}
                      ></div>
                    </div>
                    <h5 className="font-weight-bold mb-3" style={{ color: '#1e293b', fontSize: '1.1rem' }}>
                      {industry.name}
                    </h5>
                    <p style={{ color: '#64748b', fontSize: '0.95rem', lineHeight: '1.6' }}>
                      {industry.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technology Stack Section */}
      <section className="py-7">
        <div className="container">
          <div className="row">
            <div className="col-12 text-center">
              <h2 className="mb-4 font-weight-bold">{t('mobileApps.technologyStack.title')}</h2>
              <div className="position-relative overflow-hidden" style={{ height: '80px' }}>
                <motion.div
                  className="d-flex"
                  animate={{ x: ["0%", "-100%"] }}
                  transition={{ 
                    repeat: Infinity, 
                    duration: 44, 
                    ease: "linear",
                    repeatType: "loop"
                  }}
                  style={{ 
                    whiteSpace: 'nowrap', 
                    gap: '4rem',
                    width: 'max-content'
                  }}
                >
                  {/* Triple the technologies array to create truly seamless loop */}
                  {[...technologies, ...technologies, ...technologies].map((tech, index) => (
                    <span
                      key={index}
                      className="d-flex align-items-center badge badge-pill badge-lg bg-gradient-info text-white px-4 py-2"
                      style={{ 
                        fontSize: '0.9rem', 
                        minWidth: 'fit-content', 
                        gap: '0.5rem'
                      }}
                    >
                      <span style={{ fontSize: '1.2rem' }}>{tech.icon}</span>
                      <span>{tech.name}</span>
                    </span>
                  ))}
                </motion.div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* App Store Availability Section */}
      <section className="py-7" style={{ backgroundColor: '#f8fafc' }}>
        <div className="container">
          <div className="row mb-5">
            <div className="col-12 text-center">
              <h2 className="mb-4 font-weight-bold text-dark">{t('mobileApps.developmentStatus.title')}</h2>
              <p className="lead mb-0" style={{ color: '#64748b' }}>
                {t('mobileApps.developmentStatus.subtitle')}
              </p>
            </div>
          </div>

          <div className="row">
            {/* Coming Soon Q4 2025 */}
            <div className="col-lg-4 mb-4">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-header bg-success text-white text-center">
                  <h5 className="mb-0">{t('mobileApps.developmentStatus.timeline.comingSoonQ4')}</h5>
                </div>
                <div className="card-body p-4 d-flex flex-column">
                  <div className="mb-3 flex-grow-1">
                    <div className="d-flex align-items-center mb-2">
                      <span style={{ fontSize: '1.5rem', marginRight: '10px' }}>🍎</span>
                      <span className="font-weight-bold">{t('mobileApps.developmentStatus.apps.foodHealth.title')}</span>
                    </div>
                    <small className="text-muted">{t('mobileApps.developmentStatus.apps.foodHealth.description')}</small>
                  </div>
                  
                  <div className="text-center mt-auto">
                    <div className="d-flex justify-content-center align-items-center gap-2 flex-wrap">
                      {/* Google Play Badge */}
                      <div style={{ 
                        backgroundColor: '#000000', 
                        color: 'white', 
                        padding: '6px 12px', 
                        borderRadius: '6px', 
                        fontSize: '10px',
                        fontWeight: 'bold',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px',
                        opacity: '0.7',
                        minHeight: '32px',
                        border: '1px solid #ccc'
                      }}>
                        <div style={{ 
                          width: '16px', 
                          height: '16px', 
                          background: 'linear-gradient(45deg, #00D4FF 0%, #00BFFF 25%, #FFD700 50%, #FF6B35 75%, #FF1744 100%)',
                          borderRadius: '3px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '8px'
                        }}>
                          ▶
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', lineHeight: '1' }}>
                          <span style={{ fontSize: '6px', opacity: '0.8' }}>GET IT ON</span>
                          <span style={{ fontSize: '10px', fontWeight: 'bold' }}>Google Play</span>
                        </div>
                      </div>

                      {/* AppGallery Badge */}
                      <div style={{ 
                        backgroundColor: '#000000', 
                        color: 'white', 
                        padding: '6px 12px', 
                        borderRadius: '6px', 
                        fontSize: '10px',
                        fontWeight: 'bold',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px',
                        opacity: '0.7',
                        minHeight: '32px',
                        border: '1px solid #ccc'
                      }}>
                        <img src="https://developer.huawei.com/Enexport/sites/default/images/new_header/HW-LOGO.svg?v=20241104" 
                             alt="Huawei" 
                             style={{ 
                               height: '16px', 
                               width: 'auto', 
                               filter: 'brightness(0) invert(1)' 
                             }} />
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', lineHeight: '1' }}>
                          <span style={{ fontSize: '6px', opacity: '0.8' }}>EXPLORE IT ON</span>
                          <span style={{ fontSize: '10px', fontWeight: 'bold' }}>AppGallery</span>
                        </div>
                      </div>

                      {/* Apple App Store Badge */}
                      <img src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg" 
                           alt="Coming Soon on App Store" 
                           style={{ height: '32px', opacity: '0.7' }} />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* End Q4 2025 */}
            <div className="col-lg-4 mb-4">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-header bg-warning text-white text-center">
                  <h5 className="mb-0">{t('mobileApps.developmentStatus.timeline.endQ4')}</h5>
                </div>
                <div className="card-body p-4 d-flex flex-column">
                  <div className="mb-3 flex-grow-1">
                    <div className="d-flex align-items-center mb-2">
                      <span style={{ fontSize: '1.5rem', marginRight: '10px' }}>🛒</span>
                      <span className="font-weight-bold">{t('mobileApps.developmentStatus.apps.retail.title')}</span>
                    </div>
                    <small className="text-muted">{t('mobileApps.developmentStatus.apps.retail.description')}</small>
                    
                    <div className="d-flex align-items-center mb-2 mt-3">
                      <span style={{ fontSize: '1.5rem', marginRight: '10px' }}>👥</span>
                      <span className="font-weight-bold">{t('mobileApps.developmentStatus.apps.hr.title')}</span>
                    </div>
                    <small className="text-muted">{t('mobileApps.developmentStatus.apps.hr.description')}</small>
                  </div>

                  <div className="text-center mt-auto">
                    <div className="d-flex justify-content-center align-items-center gap-2 flex-wrap">
                      {/* Google Play Badge */}
                      <div style={{ 
                        backgroundColor: '#000000', 
                        color: 'white', 
                        padding: '6px 12px', 
                        borderRadius: '6px', 
                        fontSize: '10px',
                        fontWeight: 'bold',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px',
                        opacity: '0.5',
                        minHeight: '32px',
                        border: '1px solid #ccc'
                      }}>
                        <div style={{ 
                          width: '16px', 
                          height: '16px', 
                          background: 'linear-gradient(45deg, #00D4FF 0%, #00BFFF 25%, #FFD700 50%, #FF6B35 75%, #FF1744 100%)',
                          borderRadius: '3px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '8px'
                        }}>
                          ▶
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', lineHeight: '1' }}>
                          <span style={{ fontSize: '6px', opacity: '0.8' }}>GET IT ON</span>
                          <span style={{ fontSize: '10px', fontWeight: 'bold' }}>Google Play</span>
                        </div>
                      </div>

                      {/* AppGallery Badge */}
                      <div style={{ 
                        backgroundColor: '#000000', 
                        color: 'white', 
                        padding: '6px 12px', 
                        borderRadius: '6px', 
                        fontSize: '10px',
                        fontWeight: 'bold',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px',
                        opacity: '0.5',
                        minHeight: '32px',
                        border: '1px solid #ccc'
                      }}>
                        <img src="https://developer.huawei.com/Enexport/sites/default/images/new_header/HW-LOGO.svg?v=20241104" 
                             alt="Huawei" 
                             style={{ 
                               height: '16px', 
                               width: 'auto', 
                               filter: 'brightness(0) invert(1)' 
                             }} />
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', lineHeight: '1' }}>
                          <span style={{ fontSize: '6px', opacity: '0.8' }}>EXPLORE IT ON</span>
                          <span style={{ fontSize: '10px', fontWeight: 'bold' }}>AppGallery</span>
                        </div>
                      </div>

                      {/* Apple App Store Badge */}
                      <img src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg" 
                           alt="Coming Soon on App Store" 
                           style={{ height: '32px', opacity: '0.5' }} />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* In Development */}
            <div className="col-lg-4 mb-4">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-header bg-info text-white text-center">
                  <h5 className="mb-0">{t('mobileApps.developmentStatus.timeline.inDevelopment')}</h5>
                </div>
                <div className="card-body p-4 d-flex flex-column">
                  <div className="mb-3 flex-grow-1">
                    <div className="d-flex align-items-center mb-2">
                      <span style={{ fontSize: '1.5rem', marginRight: '10px' }}>⚕️</span>
                      <span className="font-weight-bold">{t('mobileApps.developmentStatus.apps.healthcare.title')}</span>
                    </div>
                    <small className="text-muted">{t('mobileApps.developmentStatus.apps.healthcare.description')}</small>
                    
                    <div className="d-flex align-items-center mb-2 mt-3">
                      <span style={{ fontSize: '1.5rem', marginRight: '10px' }}>💼</span>
                      <span className="font-weight-bold">{t('mobileApps.developmentStatus.apps.fintech.title')}</span>
                    </div>
                    <small className="text-muted">{t('mobileApps.developmentStatus.apps.fintech.description')}</small>

                    <div className="d-flex align-items-center mb-2 mt-3">
                      <span style={{ fontSize: '1.5rem', marginRight: '10px' }}>📚</span>
                      <span className="font-weight-bold">{t('mobileApps.developmentStatus.apps.education.title')}</span>
                    </div>
                    <small className="text-muted">{t('mobileApps.developmentStatus.apps.education.description')}</small>
                  </div>

                  <div className="text-center mt-auto">
                    <div className="badge badge-secondary">{t('mobileApps.developmentStatus.developmentPhase')}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Store Information */}
          <div className="row mt-4">
            <div className="col-12 text-center">
              <p className="text-muted">
                <small>
                  {t('mobileApps.developmentStatus.storeInfo.line1')}
                  <br />
                  {t('mobileApps.developmentStatus.storeInfo.line2')}
                </small>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action Section */}
      <section className="py-7 bg-gradient-dark">
        <div className="container">
          <div className="row">
            <div className="col-lg-6 mx-auto text-center">
              <h3 className="text-white mb-4">{t('mobileApps.cta.title')}</h3>
              <p className="text-white mb-4">
                {t('mobileApps.cta.description')}
              </p>
              <Link 
                to="/contact-us" 
                className="btn btn-primary btn-lg px-5 py-3"
              >
                {t('mobileApps.cta.button')}
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default MobileApps;
